<template>
  <div class="elevation-3 rounded new-section-wrapper">
    <div class="subtitle-1 font-weight-medium grey lighten-2 text-center">
      {{
        isEdit && section ? section.order : currentProgram.sections.length + 1
      }}.
      {{
        isEdit && section ? `${section.defaultTitle} (${$t('edit')})` : `(${$t('newSection')})`
      }}
    </div>

    <form class="pa-5" @submit.prevent="submit">
      <v-text-field
          outlined
          dense
          :label="$t('title')"
          class="mb-2"
          v-model="title"
          :error-messages="titleErrors"
      ></v-text-field>

      <PhotoInput
          class="mb-2"
          :photosErrors="photosErrors"
          :existingPhotosPreviews="existingPhotosPreviews"
          :isEdit="isEdit"
          @allPhotosChanged="allPhotos = $event"
          @photoToDelete="onPhotoToDelete"
      />

      <SoundInput
          class="mb-2"
          :soundsErrors="soundsErrors"
          :existingSoundsPreviews="existingSoundsPreviews"
          :isEdit="isEdit"
          @allSoundsChanged="allSounds = $event"
          @soundToDelete="onSoundToDelete"
      />

      <VideoInput
          class="mb-2"
          :videoInputs="$v.videos.$each.$iter"
          :isEdit="isEdit"
          :v="$v"
          :videosLength="videos.length"
          @addVideo="videos.push({ video: '', hasSound: false })"
          @deleteVideoInput="deleteVideoInput($event.index, $event.input)"
      />

      <div
          class="body-2 error--text my-5 font-weight-medium"
          v-if="
          $v.$dirty &&
          (!$v.allPhotos.atLeastOneAssetValidator ||
            !$v.videos.atLeastOneAssetValidator)
        "
      >
        {{ $t('sectionNeedsFile') }}
      </div>

      <div
          class="body-2 error--text my-5 font-weight-medium"
          v-if="$v.$dirty && !$v.videos.vimeoValidator"
      >
        {{ $t('invalidVimeoURL') }}
      </div>

      <div class="d-flex align-center justify-center mt-6">
        <v-btn
          class="mr-4"
          color="success"
          small
          type="submit"
          :loading="loader"
          :lang="$i18n.locale"
        >
          {{ $t('save') }}
        </v-btn>

        <v-btn
          color="warning"
          small
          type="button"
          :lang="$i18n.locale"
          @click="$emit('closeSection')"
        >
          {{ $t('cancel') }}
        </v-btn>
      </div>

      <DialogLoader
        v-if="loader"
        :open="loader"
        :text="$t('uploadingFiles')"
      />
    </form>
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import {required} from "vuelidate/lib/validators";
import DialogLoader from "@/components/DialogLoader";
import PhotoInput from "@/views/program/step2/PhotoInput";
import SoundInput from "@/views/program/step2/SoundInput";
import VideoInput from "@/views/program/step2/VideoInput";
import axios from "axios";

// ============ VALIDATORS ============

const uniqueTitleValidator = function () {
  return !this.hasUniqueTitleValidatorError;
};

const sizeValidator = function (size) {
  return function (files) {
    let isValid = true;

    files.forEach((file) => {
      const fileSize = file.size / 1024 / 1024;

      if (fileSize > size) isValid = false;
    });

    return isValid;
  };
};

const atLeastOneAssetValidator = function () {
  const hasVideos = !!this.videos.length;
  const hasPhotos = this.isEdit
      ? !!this.allPhotos.length || !!this.existingPhotosPreviews.length
      : !!this.allPhotos.length;

  return !(!hasPhotos && !hasVideos);
};

const vimeoValidator = function () {
  return !this.hasVimeoValidatorError;
};

// ============ END VALIDATORS ============

export default {
  props: ["isEdit", "section"],
  components: {
    DialogLoader,
    PhotoInput,
    SoundInput,
    VideoInput,
  },
  data() {
    return {
      title: "",
      hasUniqueTitleValidatorError: false,
      allPhotos: [],
      photosToDelete: [],
      allSounds: [],
      soundsToDelete: [],
      videos: [],
      videosToDelete: [],
      hasVimeoValidatorError: false,
      loader: false,
      socket: null,
      existingPhotosPreviews: [],
      existingSoundsPreviews: []
    };
  },
  validations: {
    title: {required, uniqueTitleValidator},
    allPhotos: {
      atLeastOneAssetValidator,
      imageSizeValidator: sizeValidator(2.5),
    },
    allSounds: {soundSizeValidator: sizeValidator(8)},
    videos: {
      atLeastOneAssetValidator,
      vimeoValidator,
      $each: {
        video: {
          required,
        },
      },
    },
  },
  computed: {
    ...mapState({
      currentProgram: (state) => state.admin.currentProgram,
      currentSection: (state) => state.admin.currentSection,
    }),
    titleErrors() {
      const errors = [];
      if (!this.$v.title.$dirty) return errors;
      if (!this.$v.title.required) errors.push(this.$t('titleIsRequired'));
      if (!this.$v.title.uniqueTitleValidator)
        errors.push(this.$t('sectionTitleExists'));
      return errors;
    },
    photosErrors() {
      const errors = [];
      if (!this.$v.allPhotos.$dirty) return errors;
      if (!this.$v.allPhotos.imageSizeValidator)
        errors.push(this.$t('fileSizeExceeded'));
      return errors;
    },
    soundsErrors() {
      const errors = [];
      if (!this.$v.allSounds.$dirty) return errors;
      if (!this.$v.allSounds.soundSizeValidator)
        errors.push(this.$t('fileSizeExceeded8mb'));
      return errors;
    },
    videosErrors() {
      const errors = [];
      if (!this.$v.videos.$dirty) return errors;
      return errors;
    },
  },
  created() {
    // this.connectToServer({
    //   brokerUrl: "ws://localhost:8080/api/ws",
    //   onConnect: () => {
    //     //const sectionSuccessTopic = "/section/success";
    //     const sectionErrorTopic = "/section/error";
    //     this.subscribeToTopic({
    //       path: "/section/success",
    //       callback: async (payload) => {
    //         console.log(`📥 ["/section/success"] ${payload}`);
    //         let soundsError = false;
    //         if (this.allSounds.length || this.soundsToDelete.length) {
    //           try {
    //             const formData = new FormData();
    //             formData.set("programId", payload.section.programId);
    //             formData.set("sectionId", payload.section.id);
    //             this.allSounds.forEach((sound) => {
    //               formData.append("sounds", sound);
    //             });
    //             this.soundsToDelete.forEach((sound) => {
    //               formData.append("soundsToDelete[]", JSON.stringify(sound));
    //             });
    //             await this.sectionSounds(formData);
    //           } catch (e) {
    //             soundsError = true;
    //             console.log(e);
    //           }
    //         }
    //         if (payload.action == "new") {
    //           console.log('new: ', payload.section);
    //           this.$store.commit("newSection", payload.section);
    //           this.toggleSnackbar({
    //             open: true,
    //             text: "Προστέθηκε νέα ενότητα!",
    //             color: "success",
    //           });
    //         } else {
    //           if ((!this.allSounds.length && !this.soundsToDelete.length) || soundsError) {
    //             // this.$store.commit("editSection", payload.section);
    //             console.log('edit: ', payload.section)
    //           }
    //           // this.toggleSnackbar({
    //           //   open: true,
    //           //   text: `Η ενότητα "${payload.section.title}" έχει ενημερωθεί`,
    //           //   color: "success",
    //           // });
    //         }
    //         this.loader = false;
    //         this.$emit("closeSection");
    //       },
    //     });
    //     this.subscribeToTopic({
    //       path: sectionErrorTopic,
    //       callback: async (payload) => {
    //         console.log(`📥 [${sectionErrorTopic}] ${payload}`);
    //         this.toggleSnackbar({
    //           open: true,
    //           text: "Κάτι πήγε στραβά. Παρακαλούμε δοκιμάστε ξανά αργότερα.",
    //           color: "error",
    //         });
    //         this.loader = false;
    //         this.$emit("closeSection");
    //       },
    //     });
    //   },
    // });
    //====== NEW SECTION INIT ======
    if (this.isEdit) {
      this.title = this.section?.defaultTitle;
      this.videos = this.section.assets
        .filter((asset) => asset.type == "LINK")
        .map((asset) => {
          return {
            video: asset.storedPath,
            hasSound: asset.hasSound,
            thumb: asset.storedThumbPath,
            id: asset.id,
          };
        });
    }
  },
  mounted() {
    this.loadExistingPhotosPreviews();
    this.loadExistingSoundsPreviews();
  },
  methods: {
    ...mapActions([
      "newSection",
      "editSection",
      "sectionSounds",
      "getProgram",
      "getPrograms",
      "updateSection"
    ]),
    ...mapActions("websocket", [
      "connectToServer",
      "disconnectFromServer",
      "subscribeToTopic",
      "unsubscribeFromTopic",
    ]),
    ...mapMutations(["toggleSnackbar"]),
    async loadExistingPhotosPreviews() {
      if (!this.isEdit || !this.section) {
        this.existingPhotosPreviews = [];
        return;
      }

      let images = this.section.assets.filter( (asset) => asset.type == "IMAGE" );

      let result = [];

      for (let k = 0; k < images.length; k++) {
        let image = images[k];
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_BASE_URL}/assets/serve/${image.storedThumbPath}`,
            {
              responseType: 'blob'
            }
          );
          const blobUrl = URL.createObjectURL(response.data);

          result.push({
            id: image.id,
            src: blobUrl,
            path: image.storedPath,
            thumb: image.thumb
          });

        } catch (err) {
          console.error(`Failed to get image #${k + 1}`, err);
        }
      }

      this.existingPhotosPreviews = result;
    },
    async loadExistingSoundsPreviews() {
      if (!this.isEdit || !this.section) {
        this.existingSoundsPreviews = [];
        return;
      }

      let sounds = this.section.assets.filter( (asset) => asset.type == "SOUND" );

      let result = [];

      for (let k = 0; k < sounds.length; k++) {
        let sound = sounds[k];

        try {
          const response = await axios.get(
            `${process.env.VUE_APP_BASE_URL}/assets/serve/${sound.storedPath}`,
            {
              responseType: 'blob'
            }
          );
          const blobUrl = URL.createObjectURL(response.data);

          result.push({
            id: sound.id,
            src: blobUrl,
            path: sound.storedPath
          });

        } catch (err) {
          console.error(`Failed to get sound #${k + 1}`, err);
        }
      }

      this.existingSoundsPreviews = result;
    },
    async deleteVideoInput(index, video) {
      if (this.isEdit && video.id) {
        // this.videosToDelete.push({id: video.id});
        try {
          await axios.delete(`${process.env.VUE_APP_BASE_URL}/assets/${video.id}`);
        } catch (e) {
          console.log(e);
        }
      }

      this.videos.splice(index, 1);  
    },
    async onPhotoToDelete(photoObject) {
      try {
        await axios.delete(`${process.env.VUE_APP_BASE_URL}/assets/${photoObject.id}`);
      } catch (e) {
        console.log(e);
      }

      this.existingPhotosPreviews = this.existingPhotosPreviews.filter((el) => {
        return el.id != photoObject.id;
      });
    },
    async onSoundToDelete(soundObject) {
      try {
        await axios.delete(`${process.env.VUE_APP_BASE_URL}/assets/${soundObject.id}`);
      } catch (e) {
        console.log(e);
      }

      this.existingSoundsPreviews = this.existingSoundsPreviews.filter((el) => {
        return el.id != soundObject.id;
      });
    },
    //SUBMIT
    async submit() {
      this.hasVimeoValidatorError = false;
      this.hasUniqueTitleValidatorError = false;

      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.loader = true;

      if (!this.isEdit) {
        // new section
        let sectionOrdering = 1;
        if (this.currentProgram.sections.length != 0) {
          sectionOrdering = this.currentProgram.sections.length + 1;
        }

        // create section first
        let data = {
          defaultLocale: 'en',
          defaultTitle: this.title,
          programId: this.currentProgram.id,
          ordering: sectionOrdering,
          locales: [
            {
              locale: 'en',
              title: this.title
            }
          ]
        }

        try {
          await this.newSection(data);
        } catch (e) {
          console.log(e);
          this.loader = false;
        }

        // create each photo section asset sequentally
        let url = `${process.env.VUE_APP_BASE_URL}/assets/`;
        let assetOrdering = 1;

        for (let i = 0; i < this.allPhotos.length; i++) {
          const photo = this.allPhotos[i];
          let photoFormData = new FormData();
          photoFormData.set("sectionId", this.currentSection.id);
          photoFormData.set("type", 'IMAGE');
          photoFormData.set("ordering", assetOrdering);
          photoFormData.set("presentationInclude", true);
          photoFormData.set("assetFile", photo);

          try {
            await axios.post(url, photoFormData);
          } catch (err) {
            console.error(`Failed to upload image #${i + 1}`, err);
          }
          assetOrdering++;
        }

        // create each audio asset sequentally
        for (let i = 0; i < this.allSounds.length; i++) {
          const sound = this.allSounds[i];
          let soundFormData = new FormData();
          soundFormData.set("sectionId", this.currentSection.id);
          soundFormData.set("type", 'SOUND');
          soundFormData.set("ordering", assetOrdering);
          soundFormData.set("presentationInclude", true);
          soundFormData.set("assetFile", sound);

          try {
            await axios.post(url, soundFormData);
          } catch (err) {
            console.error(`Failed to upload sound #${i + 1}`, err);
          }
          assetOrdering++;
        }
        
        // create each video asset sequentally
        for (let i = 0; i < this.videos.length; i++) {
          const video = this.videos[i];
          console.log(video)
          let videoFormData = new FormData();
          videoFormData.set("sectionId", this.currentSection.id);
          videoFormData.set("type", 'LINK');
          videoFormData.set("ordering", assetOrdering);
          videoFormData.set("hasSound", video.hasSound);
          videoFormData.set("presentationInclude", true);
          videoFormData.set("link", video.video);

          try {
            await axios.post(url, videoFormData);
          } catch (err) {
            console.error(`Failed to upload video #${i + 1}`, err);
          }
          assetOrdering++;
        }

        await this.getProgram(this.currentProgram.id);
        await this.getPrograms();
        this.loader = false;
        console.log('✅ All uploads complete (sequentially)');

        this.$emit("closeSection");
        this.toggleSnackbar({
          open: true,
          text: this.$t('newSectionAdded'),
          color: "success",
        });

      } else {
        // update section & create assets if something new is added
        if (this.section.title != this.title) {
          let sectionLocales = [];
          if (this.section.sectionLocales.length === 0) {
            sectionLocales.push({locale: this.section.defaultLocale, title: this.title});
          } else {
            sectionLocales = this.section.sectionLocales;
          }
          // update section first
          let data = {
            defaultTitle: this.title,
            defaultLocale: this.section.defaultLocale,
            id: this.section.id,
            ordering: this.section.ordering,
            locales: sectionLocales
          }
          try {
            await this.updateSection(data);
          } catch (e) {
            console.log(e);
            this.loader = false;
          }
        }

        // create each photo section asset sequentally
        let url = `${process.env.VUE_APP_BASE_URL}/assets/`;
        let assetOrdering = this.section.assets.length + 1;

        for (let i = 0; i < this.allPhotos.length; i++) {
          const photo = this.allPhotos[i];
          let photoFormData = new FormData();
          photoFormData.set("sectionId", this.section.id);
          photoFormData.set("type", 'IMAGE');
          photoFormData.set("ordering", assetOrdering);
          photoFormData.set("presentationInclude", true);
          photoFormData.set("assetFile", photo);

          try {
            await axios.post(url, photoFormData);
          } catch (err) {
            console.error(`Failed to upload image #${i + 1}`, err);
          }
          assetOrdering++;
        }

        // create each audio asset sequentally
        for (let i = 0; i < this.allSounds.length; i++) {
          const sound = this.allSounds[i];
          let soundFormData = new FormData();
          soundFormData.set("sectionId", this.section.id);
          soundFormData.set("type", 'SOUND');
          soundFormData.set("ordering", assetOrdering);
          soundFormData.set("presentationInclude", true);
          soundFormData.set("assetFile", sound);

          try {
            await axios.post(url, soundFormData);
          } catch (err) {
            console.error(`Failed to upload sound #${i + 1}`, err);
          }
          assetOrdering++;
        }

        // videos contains new and old videos
        let existingVideos = this.section.assets.filter((el) => {
          return el.type === 'LINK';
        });

        // create each video asset sequentally
        for (let i = 0; i < this.videos.length; i++) {
          const video = this.videos[i];
          let isVideoOld = existingVideos.find(el => el.id === video.id)
          if (!isVideoOld) {
            let videoFormData = new FormData();
            videoFormData.set("sectionId", this.section.id);
            videoFormData.set("type", 'LINK');
            videoFormData.set("ordering", assetOrdering);
            videoFormData.set("hasSound", video.hasSound);
            videoFormData.set("presentationInclude", true);
            videoFormData.set("link", video.video);

            try {
              await axios.post(url, videoFormData);
            } catch (err) {
              console.error(`Failed to upload video #${i + 1}`, err);
            }
            assetOrdering++;
          }
        }

        await this.getProgram(this.currentProgram.id);
        await this.getPrograms();
        this.loader = false;
        this.$emit("closeSection");

        this.toggleSnackbar({
          open: true,
          text: `${this.$t('theSection')} "${this.title}" ${this.$t('hasBeenUpdated')}`,
          color: "success",
        });
      }
    },
  },
  watch: {
    videos: {
      deep: true,
      handler() {
        this.hasVimeoValidatorError = false;
      },
    },
    title() {
      this.hasUniqueTitleValidatorError = false;
    },
  },
  destroyed() {
    this.disconnectFromServer()
  },
};
</script>

<style lang="scss" scoped>
.new-section-wrapper {
  overflow: hidden;
}
</style>